/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import { Rule, Ruleset } from 'types/dt-graphql-apollo-v2';
import { v1 as uuidv1 } from 'uuid';
import { gql, useApolloClient } from '@apollo/client';
import {
  createRuleQuery,
  createRulesetMutation,
} from 'utils/data-validation-graphql';
import { CopyRulesPlaceholderType, independentRulesId } from './helpers';

export type CopyRulesOptions = {
  placeholderData: CopyRulesPlaceholderType;
  allRulesets: Ruleset[];
  allRules: Rule[];
  destinationMilestoneId?: string; // used when we want to copy inside a milestone
  destinationRulesetId?: string; // used when we want to copy inside a ruleset
  onSuccess: () => void;
  onError: (err: any) => void;
};

type CreateRuleOptions = {
  rule: Rule;
  destinationMilestoneId?: string;
  parentRulesetId?: string;
  parentRulesetName?: string;
  onError: (err: any) => void;
};

type CreateRulesetOptions = {
  ruleset: Ruleset;
  newRulesetId: string;
  destinationMilestoneId?: string;
  onError: (err: any) => void;
};

type CopyRulesFunctions = {
  copyRules: (options: CopyRulesOptions) => Promise<void>;
  createRule: (options: CreateRuleOptions) => Promise<void>;
  createRuleset: (options: CreateRulesetOptions) => Promise<void>;
};

export const useCopyRules = (): CopyRulesFunctions => {
  const apolloClient = useApolloClient();

  const copyRules = async ({
    placeholderData,
    allRulesets,
    allRules,
    destinationMilestoneId,
    destinationRulesetId,
    onSuccess,
    onError,
  }: CopyRulesOptions) => {
    try {
      // If the we want to copy to the default milestone, then the destinationMilestoneId should be undefined
      const destinationId =
        destinationMilestoneId === 'Default Collection'
          ? undefined
          : destinationMilestoneId;

      // Get all the rulesets ids from each milestone
      for (const milestoneId of Object.keys(placeholderData)) {
        const rulesets = placeholderData[milestoneId];

        // Parse through the list and create new rulesets
        for (const rulesetId of Object.keys(rulesets)) {
          const newRulesetId = destinationRulesetId ?? uuidv1();
          const ruleset = allRulesets.find((r) => r.externalId === rulesetId);

          /* Handle first all the rulesets that are not the "ghost" ruleset.
          If there is a valid destinationRulesetId, then we don't need to create any new rulesets. */
          if (rulesetId !== independentRulesId && !destinationRulesetId) {
            if (ruleset) {
              await createRuleset({
                ruleset,
                newRulesetId,
                destinationMilestoneId: destinationId,
                onError: (err) => {
                  throw err;
                },
              });
            }
          }

          // Handle the rules now. They can either be independent or connected to a ruleset
          const rulesIds = placeholderData[milestoneId][rulesetId];

          let parentRulesetId: string | undefined = newRulesetId;
          if (rulesetId === independentRulesId && !destinationRulesetId) {
            parentRulesetId = undefined;
          }
          const parentRulesetName =
            rulesetId === independentRulesId ? undefined : ruleset?.name;

          for (const ruleId of rulesIds) {
            const rule = allRules.find((r) => r.externalId === ruleId);
            if (rule) {
              await createRule({
                rule,
                destinationMilestoneId: destinationId,
                parentRulesetId,
                parentRulesetName,
                onError: (err) => {
                  throw err;
                },
              });
            }
          }
        }
      }
      onSuccess();
    } catch (err: any) {
      onError(err);
    }
  };

  /** Creates a single ruleset and makes the connection to the parent milestone (if applicable). */
  const createRuleset = async ({
    ruleset,
    newRulesetId,
    destinationMilestoneId,
    onError,
  }: CreateRulesetOptions) => {
    const rulesetName = ruleset.name?.concat(' (copy)');

    const copiedRuleset = {
      fromProjectId: ruleset.projectId,
      milestoneId: destinationMilestoneId,
      externalId: newRulesetId,
      description: ruleset.description,
      name: rulesetName,
      projectId: ruleset.projectId,
      schemaType: ruleset.schemaType,
      labels: ruleset.labels,
      filters: ruleset.filters,
      space: '',
    };
    try {
      const query = createRulesetMutation(
        copiedRuleset,
        destinationMilestoneId
      );

      await apolloClient.mutate({
        mutation: gql(query),
        variables: {
          labels: ruleset.labels,
          filters: ruleset.filters,
        },
      });
    } catch (error: any) {
      onError(
        new Error(
          `Could not copy the ruleset ${ruleset.name}. ${error.toString()}`
        )
      );
    }
  };

  /** Creates a single rule and makes all the connections to the parent ruleset (if applicable) and parent milestone (if applicable). */
  const createRule = async ({
    rule,
    destinationMilestoneId,
    parentRulesetId,
    parentRulesetName,
    onError,
  }: CreateRuleOptions) => {
    const ruleName = rule.name?.concat(' (copy)');
    const ruleID = uuidv1();

    const copiedRule = {
      fromProjectId: rule.projectId,
      milestoneId: destinationMilestoneId,
      externalId: ruleID,
      customerDefinedId: rule.customerDefinedId,
      description: rule.description,
      severity: rule.severity,
      name: ruleName,
      errorMessage: rule.errorMessage,
      projectId: rule.projectId,
      schemaType: parentRulesetId ? null : rule.schemaType,
      primaryKeyName: rule.primaryKeyName,
      tags: rule.tags,
      rules: rule.rules,
      invertedRules: rule.invertedRules,
      simpleRules: rule.simpleRules,
      simpleInvertedRules: rule.simpleInvertedRules,
      advanceRules: rule.advanceRules,
      filters: rule.filters,
      space: '',
    };

    try {
      const query = createRuleQuery(
        copiedRule,
        destinationMilestoneId,
        parentRulesetId
      );

      await apolloClient.mutate({
        mutation: gql(query),
        variables: {
          tags: rule.tags,
          rules: rule.rules,
          invertedRules: rule.invertedRules,
          simpleRules: rule.simpleRules,
          simpleInvertedRules: rule.simpleInvertedRules,
          advanceRules: rule.advanceRules,
          filters: rule.filters,
        },
      });
    } catch (error: any) {
      const errorMessage =
        parentRulesetId && parentRulesetName
          ? `Could not copy the rule "${
              rule.name
            }" from the ruleset "${parentRulesetName}". ${error.toString()}`
          : `Could not copy the rule "${rule.name}". ${error.toString()}`;
      onError(new Error(errorMessage));
    }
  };

  return { copyRules, createRule, createRuleset };
};
